"use client";

import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Textarea } from "../ui/textarea";
import { X, MessageSquare, Send, Loader2, Trash2 } from "lucide-react";
import { getCompanyName } from "@/lib/textron-data-processor";
import { useAuth } from "@/contexts/AuthContext";

export interface Comment {
  question_id: string;
  user_name: string;
  datetime: string;
  text: string;
  replies: Comment[];
  /** Workspace or library name where the comment was added (e.g. "Engagement Acceptance & Continuance", "Deficiency", "Information") */
  workspace?: string;
  /** Sub-phase, sub-workspace, or item name (e.g. "Engagement Profiling", deficiency ID, procedure name) */
  sub_phase?: string;
}

interface ReviewCommentProps {
  questionId: string;
  questionText?: string;
  isOpen: boolean;
  onClose: () => void;
  /** When set, saved with each new comment so comments are associated with workspace/location */
  workspace?: string;
  /** When set, saved with each new comment (e.g. sub-workspace, library item name, procedure name) */
  subPhase?: string;
}

export function ReviewComment({ questionId, questionText, isOpen, onClose, workspace, subPhase }: ReviewCommentProps) {
  const { user } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [companyName, setCompanyName] = useState<string>("");

  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("TestClient");
      }
    };
    loadCompanyName();
  }, []);

  useEffect(() => {
    if (isOpen && questionId) {
      loadComments();
    }
  }, [isOpen, questionId]);

  const loadComments = async () => {
    if (!questionId) return;
    
    setLoading(true);
    try {
      if (window.reviewCommentsAPI?.downloadComments) {
        const result = await window.reviewCommentsAPI.downloadComments(questionId);
        if (result.success && result.data) {
          // The download script already filters comments for this question_id
          setComments(result.data.comments || []);
        } else {
          setComments([]);
        }
      } else {
        setComments([]);
      }
    } catch (error) {
      console.error("Failed to load comments:", error);
      setComments([]);
    } finally {
      setLoading(false);
    }
  };

  const saveComments = async (updatedComments: Comment[]) => {
    if (!questionId) return;
    
    setSaving(true);
    try {
      if (window.reviewCommentsAPI?.uploadComments) {
        const result = await window.reviewCommentsAPI.uploadComments(questionId, updatedComments);
        if (result.success) {
          setComments(updatedComments);
          setNewComment("");
          setReplyingTo(null);
          setReplyText("");
        } else {
          throw new Error(result.error || "Failed to save comments");
        }
      } else {
        throw new Error("Review Comments API not available");
      }
    } catch (error) {
      console.error("Failed to save comments:", error);
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      console.error("Error details:", errorMessage);
      alert(`Failed to save comments: ${errorMessage}. Please try again.`);
    } finally {
      setSaving(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    const comment: Comment = {
      question_id: questionId,
      user_name: user?.user_name || "Anonymous",
      datetime: new Date().toISOString(),
      text: newComment.trim(),
      replies: [],
      ...(workspace !== undefined && { workspace }),
      ...(subPhase !== undefined && { sub_phase: subPhase }),
    };

    const updatedComments = [...comments, comment];
    await saveComments(updatedComments);
  };

  const handleReply = async (parentComment: Comment, replyIndex?: number) => {
    if (!replyText.trim()) return;

    const reply: Comment = {
      question_id: questionId,
      user_name: user?.user_name || "Anonymous",
      datetime: new Date().toISOString(),
      text: replyText.trim(),
      replies: [],
      ...(workspace !== undefined && { workspace }),
      ...(subPhase !== undefined && { sub_phase: subPhase }),
    };

    const updatedComments = comments.map((c) => {
      if (c === parentComment) {
        return {
          ...c,
          replies: [...c.replies, reply]
        };
      }
      return c;
    });

    await saveComments(updatedComments);
  };

  const handleNestedReply = async (parentComment: Comment, replyToReply: Comment) => {
    if (!replyText.trim()) return;

    const nestedReply: Comment = {
      question_id: questionId,
      user_name: user?.user_name || "Anonymous",
      datetime: new Date().toISOString(),
      text: replyText.trim(),
      replies: [],
      ...(workspace !== undefined && { workspace }),
      ...(subPhase !== undefined && { sub_phase: subPhase }),
    };

    const updatedComments = comments.map((c) => {
      if (c === parentComment) {
        return {
          ...c,
          replies: c.replies.map((r) => {
            if (r === replyToReply) {
              return {
                ...r,
                replies: [...r.replies, nestedReply]
              };
            }
            return r;
          })
        };
      }
      return c;
    });

    await saveComments(updatedComments);
  };

  const handleDeleteComment = async (commentToDelete: Comment) => {
    if (!window.confirm("Are you sure you want to delete this comment and all its replies?")) {
      return;
    }

    const updatedComments = comments.filter((c) => c !== commentToDelete);
    await saveComments(updatedComments);
  };

  const handleDeleteReply = async (parentComment: Comment, replyToDelete: Comment) => {
    if (!window.confirm("Are you sure you want to delete this reply and all its nested replies?")) {
      return;
    }

    // Recursive function to remove a reply from a comment's replies array (handles nested replies)
    const removeReply = (comment: Comment, replyToRemove: Comment): Comment => {
      if (comment.replies && comment.replies.length > 0) {
        return {
          ...comment,
          replies: comment.replies
            .filter((r) => r !== replyToRemove)
            .map((r) => removeReply(r, replyToRemove)) // Recursively check nested replies
        };
      }
      return comment;
    };

    const updatedComments = comments.map((c) => {
      // If this is the parent comment, remove the reply from it
      if (c === parentComment) {
        return removeReply(c, replyToDelete);
      }
      // Otherwise, recursively check if the reply exists in nested structure
      return removeReply(c, replyToDelete);
    });

    await saveComments(updatedComments);
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const renderComment = (comment: Comment, isReply = false, parentComment?: Comment) => {
    return (
      <div key={`${comment.datetime}-${comment.user_name}`} className={`${isReply ? 'ml-6 mt-3' : 'mb-4'}`}>
        <Card className={`border-white/10 bg-white/5 ${isReply ? 'border-l-2 border-l-[#fbbf24]/30' : ''}`}>
          <CardContent className="p-4">
            <div className="space-y-2">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-sm font-medium text-white">{comment.user_name}</div>
                  <div className="text-xs text-white/60">{formatDateTime(comment.datetime)}</div>
                </div>
                <div className="flex items-center gap-2">
                  {!isReply ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteComment(comment)}
                      className="text-xs text-red-400 hover:text-red-300 hover:bg-red-500/20"
                      disabled={saving}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  ) : parentComment && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteReply(parentComment, comment)}
                      className="text-xs text-red-400 hover:text-red-300 hover:bg-red-500/20"
                      disabled={saving}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="text-sm text-white/90 whitespace-pre-wrap">{comment.text}</div>
              {!isReply && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setReplyingTo(comment.datetime);
                    setReplyText("");
                  }}
                  className="text-xs text-[#fbbf24] hover:text-[#fbbf24]/80 hover:bg-white/5"
                >
                  Reply
                </Button>
              )}
              {replyingTo === comment.datetime && !isReply && (
                <div className="mt-2 space-y-2">
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Write a reply..."
                    className="text-white border-white/10 bg-black/40 min-h-[80px] resize-none"
                    disabled={saving}
                    data-allow-archival-edit={true}
                    onFocus={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleReply(comment)}
                      disabled={!replyText.trim() || saving}
                      className="bg-[#fbbf24] text-white hover:bg-[#fbbf24]/80"
                    >
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      Post Reply
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setReplyingTo(null);
                        setReplyText("");
                      }}
                      className="border-white/20 text-white/80 hover:bg-white/10"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
              {comment.replies && comment.replies.length > 0 && (
                <div className="mt-3 space-y-2">
                  {comment.replies.map((reply) => (
                    <div key={`${reply.datetime}-${reply.user_name}`}>
                      {renderComment(reply, true, comment)}
                      {replyingTo === reply.datetime && (
                        <div className="mt-2 ml-6 space-y-2">
                          <Textarea
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder="Write a reply..."
                            className="text-white border-white/10 bg-black/40 min-h-[80px] resize-none"
                            disabled={saving}
                            data-allow-archival-edit={true}
                            onFocus={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleNestedReply(parentComment!, reply)}
                              disabled={!replyText.trim() || saving}
                              className="bg-[#fbbf24] text-white hover:bg-[#fbbf24]/80"
                            >
                              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                              Post Reply
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setReplyingTo(null);
                                setReplyText("");
                              }}
                              className="border-white/20 text-white/80 hover:bg-white/10"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                      {replyingTo !== reply.datetime && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setReplyingTo(reply.datetime);
                            setReplyText("");
                          }}
                          className="ml-6 text-xs text-[#fbbf24] hover:text-[#fbbf24]/80 hover:bg-white/5"
                        >
                          Reply
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={(e) => {
        // Close modal when clicking backdrop
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <Card 
        className="w-full max-w-3xl max-h-[90vh] border-white/10 bg-black/80 text-white shadow-2xl overflow-hidden flex flex-col relative z-[10000]"
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader className="flex flex-row items-center justify-between border-b border-white/10 flex-shrink-0">
          <div className="flex items-center gap-3">
            <MessageSquare className="h-5 w-5 text-[#fbbf24]" />
            <CardTitle className="text-lg">Review Comments</CardTitle>
            {questionText && (
              <span className="text-sm text-white/60 font-normal">- {questionText.substring(0, 50)}...</span>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-white/60 hover:text-white"
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        
        <CardContent className="p-6 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-[#fbbf24]" />
              <span className="ml-2 text-white/60">Loading comments...</span>
            </div>
          ) : (
            <div className="space-y-4">
              {comments.length === 0 ? (
                <div className="text-center py-8 text-white/60">
                  No comments yet. Be the first to comment!
                </div>
              ) : (
                comments.map((comment) => renderComment(comment))
              )}
            </div>
          )}
        </CardContent>

        <div className="p-6 border-t border-white/10 flex-shrink-0">
          <div className="space-y-2">
            <Textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Write a comment..."
              className="text-white border-white/10 bg-black/40 min-h-[100px] resize-none"
              disabled={saving}
              data-allow-archival-edit={true}
              onFocus={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={onClose}
                className="border-white/20 text-white/80 hover:bg-white/10"
              >
                Close
              </Button>
              <Button
                onClick={handleAddComment}
                disabled={!newComment.trim() || saving}
                className="bg-[#fbbf24] text-white hover:bg-[#fbbf24]/80"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Posting...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Post Comment
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
